import type { ICaptionWord, ITrack } from "@designcombo/types";
import { Phase } from "@repo/types";

export interface CaptionStyleExtractorOptions {
	currentFrame: number;
	startAtFrame: number;
	endAtFrame: number;
	fps: number;
	track?: ITrack;
	word?: ICaptionWord;
	// Interpolation functions passed as parameters
	interpolate: (frame: number, input: number[], output: number[], options?: any) => number;
	interpolateColors: (frame: number, input: number[], output: string[]) => string;
	easing?: {
		out: (easing: any) => any;
		quad: any;
		linear?: any;
		in?: (easing: any) => any;
		inOut?: (easing: any) => any;
	};
}

export interface CaptionStyleExtractorResult {
	style: Record<string, any>;
	hasChanges: boolean;
}

/**
 * Extracts and interpolates styles from track styles for caption components
 * This function handles the three-phase styling system (initial, active, past)
 * and provides smooth transitions between phases
 */
export function extractCaptionStyles(options: CaptionStyleExtractorOptions): CaptionStyleExtractorResult {
	const { currentFrame, startAtFrame, endAtFrame, fps, track, interpolate, interpolateColors, easing, word } = options;
	
	/**
	 * Determines the current phase of the caption word
	 */
	function getPhase(): Phase {
		if (currentFrame <= startAtFrame) {
			return Phase.INITIAL;
		}
		if (currentFrame > startAtFrame && currentFrame < endAtFrame) {
			return Phase.ACTIVE;
		}
		return Phase.PAST;
	}

	/**
	 * Maps easing string to easing function
	 */
	function getEasingFunction(easingString?: string): ((t: number) => number) | undefined {
		if (!easing || !easingString) {
			return easing?.out?.(easing?.quad);
		}

		// Map easing strings to Remotion easing functions
		switch (easingString) {
			case "linear":
				// Use linear easing if available, otherwise use quad (which is close to linear)
				return easing.linear || easing.quad;
			case "ease-in":
				// Use ease-in if available, otherwise fallback to quad
				return easing.in?.(easing.quad) || easing.quad;
			case "ease-out":
				// Use ease-out-quad
				return easing.out?.(easing.quad);
			case "ease-in-out":
				// Use ease-in-out if available, otherwise use ease-out
				return easing.inOut?.(easing.quad) || easing.out?.(easing.quad);
			default:
				return easing.out?.(easing.quad);
		}
	}

	/**
	 * Interpolates between two phase options
	 */
	function getInterpolation(
		phaseOptions: any,
		previousPhaseOptions: any
	) {
		const prevComposedValues = previousPhaseOptions?.value?.split(",");
		const composedValues = phaseOptions?.value?.split(",");
		let duration = fps * (phaseOptions?.duration ?? 0.3);
		const phaseEasing = phaseOptions?.options?.easing;
		let result = "";
		
		composedValues?.forEach((composedValue: any, composedValueIndex: number) => {
			const resultValue = composedValue
				?.split(" ")
				.map((itemValue: any, valueIndex: number) => {
					const isColor = ["#", "rbg(", "rbga(", "hsl(", "hsla("].some(
						(param: string) => itemValue.startsWith(param)
					);
					const prevValue =
						prevComposedValues?.[composedValueIndex]?.split(" ")[valueIndex];
					if (isColor) {
						duration = 0 === duration ? 1 : duration;
						const currentItemColor = new Option().style;
						const prevItemColor = new Option().style;
						prevItemColor.color = prevValue || '';
						currentItemColor.color = itemValue || '';
						return interpolateColors(
							currentFrame,
							[startAtFrame, startAtFrame + duration],
							[prevItemColor.color || 'white', `${currentItemColor.color || 'white'}`]
						);
					}
					return interPolateValue(prevValue!, itemValue, duration, phaseEasing);
				})
				.join(" ");
			result += (result ? ", " : "") + resultValue;
		});
		return result;
	}

	/**
	 * Interpolates numeric values with CSS functions support
	 * Fixed to properly handle transform properties like scale(x), translate(x, y), etc.
	 */
	function interPolateValue(prev: string, current: string, duration: number, phaseEasing?: string) {
		// Handle empty or invalid values
		if (!prev || !current) {
			return current || prev;
		}

		const hasCSSFunction =
			current.indexOf("(") > -1 && current.indexOf(")") > -1;
		
		let cssFunction = "";
		let currentValue = current;
		let prevValue = prev;
		
		if (hasCSSFunction) {
			// Extract the CSS function name (e.g., "scale", "translate")
			const openParenIndex = currentValue.indexOf("(");
			const closeParenIndex = currentValue.indexOf(")");
			
			if (openParenIndex > -1 && closeParenIndex > -1) {
				cssFunction = currentValue.substring(0, openParenIndex);
				// Extract the content inside parentheses
				currentValue = currentValue.substring(openParenIndex + 1, closeParenIndex);
				prevValue = prevValue.substring(prevValue.indexOf("(") + 1, prevValue.indexOf(")"));
			}
		}
		
		// Handle multiple values (e.g., translate(x, y))
		const currentValues = currentValue.split(",").map(v => v.trim());
		const prevValues = prevValue.split(",").map(v => v.trim());
		
		const interpolatedValues: string[] = [];
		
		for (let i = 0; i < Math.max(currentValues.length, prevValues.length); i++) {
			const currentVal = currentValues[i] || "0";
			const prevVal = prevValues[i] || "0";
			
			// Extract numeric value and unit
			const currentMatch = currentVal.match(/(-?\d*\.?\d+)(.*)/);
			const prevMatch = prevVal.match(/(-?\d*\.?\d+)(.*)/);
			
			if (currentMatch && prevMatch) {
				const currentNum = Number.parseFloat(currentMatch[1]);
				const prevNum = Number.parseFloat(prevMatch[1]);
				const unit = currentMatch[2] || prevMatch[2] || "";
				
				if (!Number.isNaN(currentNum) && !Number.isNaN(prevNum)) {
					const startFrame = getPhase() === Phase.PAST ? endAtFrame : startAtFrame;
					const _duration = duration === 0 ? 1 : duration;
					
					const easingFunction = getEasingFunction(phaseEasing);
					const interpolatedValue = interpolate(
						currentFrame,
						[startFrame, startFrame + _duration],
						[prevNum, currentNum],
						{
							easing: easingFunction,
							extrapolateLeft: "clamp",
							extrapolateRight: "clamp",
						}
					);
					
					interpolatedValues.push(interpolatedValue + unit);
				} else {
					interpolatedValues.push(currentVal);
				}
			} else {
				interpolatedValues.push(currentVal);
			}
		}
		
		// Reconstruct the CSS function
		if (hasCSSFunction) {
			return `${cssFunction}(${interpolatedValues.join(", ")})`;
		}
		
		return interpolatedValues.join(", ");
	}

	/**
	 * Gets the target phase options based on current phase
	 */
	function getTargetOptions(rootPhase: any): { 
		previousPhase: any,
		currentPhase: any 
	} {
		let previousPhase: any;
		let currentPhase: any = rootPhase; // initial by default
		
		switch (getPhase()) {
			case Phase.ACTIVE:
				previousPhase = rootPhase;
				currentPhase = rootPhase.active?.value ? rootPhase.active : previousPhase;
				break;
			case Phase.PAST:
				previousPhase = rootPhase.active?.value ? rootPhase.active : rootPhase;
				currentPhase = rootPhase.past?.value ? rootPhase.past : previousPhase;
				break;
			default:
				currentPhase = rootPhase; // initial by default
		}
		
		return {
			previousPhase,
			currentPhase,
		};
	}

	/**
	 * Builds style properties out of the track styles
	 * Enhanced to better handle transform and other complex properties
	 */
	function extractStyle(): Record<string, any> {
		if (!track?.styles || !Array.isArray(track.styles)) {
			return {};
		}

		const resultStyle = track.styles.reduce((style: Record<string, any>, property: any) => {
			if (!property || !property.property) {
				return style;
			}

			const { currentPhase, previousPhase } = getTargetOptions(property);
			let newStyle = style;
			
			if (currentPhase?.value && property.property) {
				let value = currentPhase.value;
				
				// Apply interpolation if duration is specified and we have a previous phase
				if (currentPhase.duration && previousPhase && previousPhase.value) {
					try {
						value = getInterpolation(currentPhase, previousPhase);
					} catch (error) {
						console.warn(`Failed to interpolate property ${property.property}:`, error);
						// Fallback to current value if interpolation fails
						value = currentPhase.value;
					}
				}
				
				newStyle = Object.assign({}, style, { [property.property]: value });
			}
			
			return newStyle;
		}, {}) as Record<string, any>;
		
		return resultStyle;
	}

	const style = extractStyle();
	
	// Debug logging to help troubleshoot transform properties
	if (Object.keys(style).length > 0 && word?.word.toLowerCase() === 'attention') {
		console.log('CaptionStyleExtractor - Extracted styles:', {
			currentFrame,
			startAtFrame,
			endAtFrame,
			phase: getPhase(),
			styles: style,
			trackStyles: track?.styles
		});
	}
	
	return {
		style,
		hasChanges: true, // This will be determined by the calling component
	};
}

/**
 * Hook-like function for extracting styles with change detection
 * This can be used in React components to efficiently update styles
 */
export function useCaptionStyleExtractor(options: CaptionStyleExtractorOptions): CaptionStyleExtractorResult {
	return extractCaptionStyles(options);
} 